import { useQuery } from "@tanstack/react-query";
import { hasCustomerId } from "@/lib/api";
import { fetchPurchasedProductDetail } from "@/lib/api";

export interface ShippingAddress {
  first_name: string;
  last_name: string;
  address1: string;
  address2: string | null;
  city: string;
  country: string;
  country_code?: string | null;
  province: string;
  province_code?: string | null;
  zip: string;
  phone: string | null;
}

export interface Customer {
  email: string;
  shipping_address: ShippingAddress | null;
}

/** A discount applied to a subscription line (e.g. the retention offer), from Stay AI. */
export interface SubscriptionDiscount {
  code?: string | null;
  percentage?: number | null;
  type?: string | null;
  target_type?: string | null;
}

export interface SellingPlanOption {
  name: string;
  value: string;
}

export interface SellingPlanPriceAdjustment {
  adjustment_type: "PERCENTAGE" | "PRICE";
  adjustment_value: {
    percentage?: number;
    amount?: string;
    currency_code?: string;
  };
}

export interface SellingPlan {
  shopify_id: string;
  name: string;
  options?: SellingPlanOption[];
  price_adjustments?: SellingPlanPriceAdjustment[];
}

export interface SellingPlanGroup {
  title: string;
  selling_plans: SellingPlan[];
}

export interface Variant {
  shopify_id: number;
  title: string;
  price: number;
  selected_options: Array<{
    name: string;
    value: string;
  }>;
  is_subscription?: boolean;
  selling_plans?: SellingPlan[];
  selling_plan_groups?: SellingPlanGroup[];
  discount_percentage?: number | null;
  discount_amount?: number | null;
  /** Present only when a discount code is applicable to this variant */
  price_after_discount?: number | null;
}

export interface Option {
  id: string;
  name: string;
  values: string[];
  position: number;
}

export type WidgetValuePlacement = "main" | "buy_once" | "both";

export interface WidgetValueConfig {
  display: string;
  hidden?: boolean;
  badge?: string;
  placement?: WidgetValuePlacement;
  hide_buy_once_options?: boolean;
}

export interface WidgetOptionConfig {
  name: string;
  hidden?: boolean;
  buy_once_only?: boolean;
  label?: string;
  fallback_value?: string;
  value_labels?: Record<string, WidgetValueConfig>;
}

export interface VisibilityRule {
  when_option: string;
  when_value: string;
  target_option: string;
  hidden_values: string[];
}

export interface VariantSellingPlanExclusion {
  variant_shopify_id: number;
  excluded_selling_plan_shopify_ids: string[];
}

export interface SellingPlanVisibilityRule {
  when_option: string;
  when_value: string;
  hidden_selling_plan_shopify_ids: string[];
}

export interface WidgetConfig {
  option_configs: WidgetOptionConfig[];
  buy_once_variant_shopify_ids: number[];
  visibility_rules?: VisibilityRule[];
  variant_selling_plan_exclusions?: VariantSellingPlanExclusion[];
  selling_plan_visibility_rules?: SellingPlanVisibilityRule[];
  most_popular_selling_plan_ids?: string[];
  selling_plan_discount_overrides?: SellingPlanDiscountOverride[];
  variant_base_prices?: VariantBasePrice[];
  selling_plan_base_prices?: SellingPlanBasePrice[];
}

export interface SellingPlanDiscountOverride {
  selling_plan_shopify_id: string;
  discount_percentage: number;
}

export interface VariantBasePrice {
  variant_shopify_id: number;
  base_price: number;
}

export interface SellingPlanBasePrice {
  selling_plan_shopify_id: string;
  base_price: number;
}

export interface PurchasedProductDetail {
  id: number;
  variant_id: number;
  title: string;
  image_url: string;
  price: number;
  last_bought_at: string;
  is_favorite: boolean;
  favorite_id: number | null;
  shop_domain: string;
  shopify_shop_domain: string;
  product_sku: string;
  customer: Customer;
  variants: Variant[];
  options: Option[];
  is_subscription?: boolean;
  /** "active" | "paused" | "cancelled" | null - null means one-time purchase */
  subscription_status?: string | null;
  subscription_next_billing_date?: string | null;
  subscription_last_charge_date?: string | null;
  subscription_paused_until?: string | null;
  paused_until?: string | null;
  subscription_order_count?: number | null;
  can_skip_next_order?: boolean | null;
  subscription_max_reschedule_date?: string | null;
  subscription_unit_price?: number | null;
  subscription_one_time_price?: number | null;
  subscription_discounted_price?: number | null;
  subscription_discounts?: SubscriptionDiscount[] | null;
  subscription_billing_interval?: string | null;
  subscription_billing_interval_count?: number | null;
  subscription_cancellation_requested_at?: string | null;
  /** Set once the customer accepts the product-swap save offer (ISO). */
  subscription_swap_requested_at?: string | null;
  subscription_shipping_address?: ShippingAddress | null;
  subscription_conversion_fee_paid?: boolean | null;
  /** ISO time the cancel was scheduled to run after the next delivery; null when none. */
  subscription_cancel_scheduled_at?: string | null;
  subscription_is_flexible?: boolean | null;
  shopify_subscription_id?: number | null;
  discount_percentage?: number | null;
  discount_amount?: number | null;
  /** Present only when a discount code is applicable to this product */
  price_after_discount?: number | null;
  /** Unit price as recorded on the order's line item (preferred over `price` for what the customer actually paid). */
  order_line_item_price?: number | null;
  widget_config?: WidgetConfig | null;
}

export function usePurchasedProductDetail(
  productId: number | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["purchased-product-detail", productId],
    queryFn: async (): Promise<PurchasedProductDetail | null> => {
      if (!productId) return null;
      const result = await fetchPurchasedProductDetail(productId);
      return result as PurchasedProductDetail | null;
    },
    enabled: enabled && productId !== null && hasCustomerId(),
  });
}
